/**
 * PulseRoutinesCard — routine-health list for the Pulse Home Dashboard.
 *
 * A list of the workspace's routines, each with an asset-coverage progress bar
 * (blue engagement ramp from the Crop Circle info family) and a percentage —
 * coverage = distinct assets that submitted this routine in the selected period
 * / assets the routine is assigned to (so the card responds to the time
 * selector).
 *
 * Map-plottable: pressing the header plots assets that have routine activity,
 * coloured by routine. REAL, workspace-scoped — consumes `useHomeCards(range)`.
 *
 * Design matches the gripp-os PulseHomeMapView Routines card:
 *  - RoutineHealthList layout (name + ProgressBar + percentage per row)
 *  - Pressing a row selects that routine (filters map in Phase 2 wiring)
 *  - Pressing the header selects the card's plotting mode
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Colors, tokens, Skeleton, EmptyState, ErrorState } from '@gripp/shared-ui';
import { DashboardCardShell, ViewAllLink } from './dashboardCardShell';
import { ProgressBar } from './cardPrimitives';
import { useHomeCards } from '../hooks/useHomeCards';
import { HomeCardProps } from '../types';

// Routine completion → blue tier (Crop Circle info family). Darker = healthier.
function engagementColor(rate: number): string {
  if (rate >= 95) return tokens.colors.semantic.infoDark; // #1F5B99
  if (rate >= 80) return tokens.colors.semantic.info; // #4A90E2
  return `${tokens.colors.semantic.info}8C`; // muted info blue (~55%) — needs-attention
}

export const PulseRoutinesCard: React.FC<HomeCardProps> = ({
  range,
  selected,
  onToggleSelect,
  activeStatus,
  onSelectStatus,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useHomeCards(range);
  const routines = data?.routines;

  const title = t('home.routines.title', 'Routines');
  const action = (
    <ViewAllLink
      label={t('home.cards.viewAll', 'View all')}
      onPress={() => navigate('/new-routines')}
      testID="pulse-routines-card-view-all"
    />
  );

  if (isLoading) {
    return (
      <DashboardCardShell
        title={title}
        action={action}
        testID="pulse-routines-card"
        loadingSlot={
          <View style={styles.loading}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={18} radius={4} />
            ))}
          </View>
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  if (error) {
    return (
      <DashboardCardShell
        title={title}
        action={action}
        testID="pulse-routines-card"
        errorSlot={
          <ErrorState
            title={t('home.cards.error', 'Could not load data')}
            testID="pulse-routines-card-error"
          />
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  if (!routines || routines.items.length === 0) {
    return (
      <DashboardCardShell
        title={title}
        action={action}
        testID="pulse-routines-card"
        emptySlot={
          <EmptyState
            title={t('home.routines.empty', 'No routines')}
            testID="pulse-routines-card-empty"
          />
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  return (
    <DashboardCardShell
      title={title}
      subtitle={String(routines.total)}
      action={action}
      testID="pulse-routines-card"
      onHeaderPress={onToggleSelect}
      selected={selected}
      headerPressLabel={t('home.routines.plot', 'Plot routine activity on the map')}
    >
      <ScrollView
        style={styles.list}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
      >
        {routines.items.map((r) => {
          const isActive = activeStatus === r.name;
          return (
            <Pressable
              key={r.id}
              style={[
                styles.row,
                isActive && styles.rowActive,
                activeStatus && !isActive ? styles.rowDimmed : null,
              ]}
              onPress={() => onSelectStatus?.(r.name)}
              accessibilityRole="button"
              accessibilityLabel={t(
                'home.routines.filterBy',
                'Plot {{name}} on the map',
                { name: r.name }
              )}
            >
              <Text
                style={[styles.name, isActive && styles.nameActive]}
                numberOfLines={1}
              >
                {r.name}
              </Text>
              <ProgressBar
                value={r.completionRate}
                max={100}
                color={engagementColor(r.completionRate)}
              />
              <Text style={styles.pct}>{r.completionRate}%</Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </DashboardCardShell>
  );
};

PulseRoutinesCard.displayName = 'PulseRoutinesCard';

const styles = StyleSheet.create({
  loading: {
    gap: tokens.spacing.sm,
  },
  // -8 margin / +8 padding so the row hover/selection highlight bleeds 8px into
  // the card gutter on both sides (matches the reference -mx-2 px-2).
  list: {
    flex: 1,
    marginHorizontal: -tokens.spacing.sm,
  },
  listContent: {
    paddingHorizontal: tokens.spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.sm, // py-2 (8px)
    paddingHorizontal: tokens.spacing.sm, // px-2 (8px)
    marginHorizontal: -tokens.spacing.sm, // -mx-2
    borderRadius: tokens.borderRadius.sm,
  },
  rowActive: {
    backgroundColor: Colors.role.surfaceInverseSoft,
  },
  rowDimmed: {
    opacity: 0.45,
  },
  name: {
    ...tokens.typography.label.medium,
    fontSize: 13,
    lineHeight: 18,
    color: Colors.role.textStrong,
    flex: 1,
  },
  nameActive: {
    fontWeight: tokens.typography.label.mediumEmphasised.fontWeight,
    color: Colors.role.textStrong,
  },
  pct: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    color: Colors.role.textSoft,
    width: 36,
    textAlign: 'right',
    fontVariant: ['tabular-nums'],
  },
});
